'use client';

import { useState } from 'react';
import { toast } from 'react-toastify';
import { encodeFunctionData, parseUnits } from 'viem';

import { useAuth } from '@/hooks/useAuth';
import { usePolymarketClient } from '@/hooks/usePolymarketClient';
import { walletApi } from '@/services/api/wallet.api';
import { executeRelayMetaTxBatch } from '@/utils/relayerBatch.utils';

import { MESSAGES } from '@/constants/messages.constants';

const ERC20_ABI = [
  {
    constant: false,
    inputs: [
      { name: '_to', type: 'address' },
      { name: '_value', type: 'uint256' },
    ],
    name: 'transfer',
    outputs: [{ name: '', type: 'bool' }],
    type: 'function',
  },
] as const;

export function useTransferFunds() {
  const { user } = useAuth();
  const { client } = usePolymarketClient();
  const [isPending, setIsPending] = useState(false);

  const transfer = async (
    tokenAddress: `0x${string}`,
    recipientAddress: `0x${string}`,
    amount: string
  ) => {
    if (!client || !user?.proxyWalletAddress) {
      toast.error(MESSAGES.TOAST.TRADING_CLIENT_PROXY_NOT_READY);
      throw new Error('Not ready');
    }

    setIsPending(true);
    try {
      // Both pUSD and USDC.e use 6 decimals on Polygon
      const amountInWei = parseUnits(amount, 6);

      const data = encodeFunctionData({
        abi: ERC20_ABI,
        functionName: 'transfer',
        args: [recipientAddress, amountInWei],
      });

      const transactions = [
        {
          to: tokenAddress,
          data,
          value: '0',
        },
      ];

      toast.info(MESSAGES.TOAST.SUBMITTING_TRANSFER);

      const response = await executeRelayMetaTxBatch(
        client as any,
        user.proxyWalletAddress,
        user.tradingWalletKind,
        transactions,
        'Direct transfer to wallet'
      );

      // Depending on the relayer response type, we may need to wait
      if (response && typeof response.wait === 'function') {
        toast.info(MESSAGES.TOAST.TRANSACTION_WAITING);
        await response.wait();
      }

      toast.success(MESSAGES.TOAST.TRANSFER_SUCCESSFUL);
    } catch (error: any) {
      console.error('Transfer error:', error);
      let msg = error?.message || 'Failed to transfer funds';
      if (msg.toLowerCase().includes('user rejected') || error?.code === 4001) {
        msg = 'Transaction rejected by user.';
      }
      toast.error(msg);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { transfer, isPending };
}
